import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { db } from "@/services/firebaseService";
import { BulkAddSessionsFormValues } from "../schemas/bulkAddSessionsSchema";
import { generateSessionDates } from "../utils/sessionGenerator";
import { calculateEndDateFromNumDays } from "./utils/sessionDateUtils";
import { CampLocation } from "@/types/campLocation";
import { createOffer, getOffersByLocationId } from "@/services/offer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { utcEpochDayKey } from "@/utils/dateEpochKey";
import { doc, getDoc } from "firebase/firestore";
import {
  buildTier0SourceKeyFromUrl,
  buildTier1IdempotencyKey,
  numDaysFromDatesUtcInclusive,
  normalizeUrlForKey,
} from "../utils/orgBulkIdempotency";

interface UseBulkSessionsCreationProps {
  locationId: string;
  location: CampLocation;
  userId?: string; // Make userId optional since we'll get it from auth context
  onSuccess: () => void;
}

export const useBulkSessionsCreation = ({
  locationId,
  location,
  userId: providedUserId, // Rename to providedUserId to avoid confusion
  onSuccess
}: UseBulkSessionsCreationProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const { toast: legacyToast } = useToast(); // Keep for backward compatibility
  const { currentUser } = useAuth(); // Get current user from auth context

  // Function to create bulk sessions
  const createBulkSessions = async (
    formValues: BulkAddSessionsFormValues,
    generatedDates: Date[]
  ) => {
    
    setIsCreating(true);
    
    // Get the user ID from context or from props (for backward compatibility)
    const userId = providedUserId || currentUser?.uid;
    
    if (!userId) {
      console.error("No user ID available for creating sessions");
      toast.error("Cannot create sessions: User not authenticated");
      setIsCreating(false);
      return;
    }
    
    try {
      // Show loading indicator for longer operations
      const loadingToast = generatedDates.length > 5 ? 
        toast.loading(`Creating ${generatedDates.length} sessions...`) : null;
      
      
      // Track successfully created sessions
      let successCount = 0;
      let skippedCount = 0;
      const failedDates: Date[] = [];
      // When using RowFormat with duplicate dates, dateListExtras can contain
      // an array of per-row extras objects for a single epoch day. We track
      // how many sessions we've already created per day so we can pull the
      // correct extras object in order.
      const perDayRowIndex = new Map<string, number>();

      const variationMapByLocationId = new Map<string, Map<string, any>>();
      const loadVariationMap = async (targetLocationId: string): Promise<Map<string, any>> => {
        const locId = String(targetLocationId || "").trim();
        if (!locId) return new Map<string, any>();
        const cached = variationMapByLocationId.get(locId);
        if (cached) return cached;

        let variations: any[] = [];
        if (locId === locationId) {
          variations = Array.isArray(location.variations) ? location.variations : [];
        } else {
          const snap = await getDoc(doc(db as any, "campLocations", locId));
          if (!snap.exists()) throw new Error(`Target location ${locId} not found`);
          const data = snap.data() as any;
          variations = Array.isArray(data?.variations) ? data.variations : [];
        }
        const map = new Map<string, any>();
        for (const v of variations) {
          const k = typeof v?.key === "string" ? v.key.trim() : "";
          if (k) map.set(k, v);
        }
        variationMapByLocationId.set(locId, map);
        return map;
      };

      const existingKeysByLocationId = new Map<
        string,
        { sourceKeys: Set<string>; tier1Keys: Set<string> }
      >();
      const coerceToDate = (v: any): Date | null => {
        if (!v) return null;
        if (v instanceof Date) return isNaN(v as any) ? null : v;
        if (typeof v?.toDate === "function") {
          const d = v.toDate();
          return d instanceof Date && !isNaN(d as any) ? d : null;
        }
        const d = new Date(v);
        return isNaN(d as any) ? null : d;
      };
      const loadExistingKeys = async (targetLocationId: string) => {
        const locId = String(targetLocationId || "").trim();
        if (!locId) return { sourceKeys: new Set<string>(), tier1Keys: new Set<string>() };
        const cached = existingKeysByLocationId.get(locId);
        if (cached) return cached;

        const offers = await getOffersByLocationId(db as any, locId);
        const sourceKeys = new Set<string>();
        const tier1Keys = new Set<string>();
        for (const o of offers) {
          const src = String((o as any)?.sourceKey || "").trim();
          if (src) sourceKeys.add(src);
          const derivedSrc = buildTier0SourceKeyFromUrl((o as any)?.url);
          if (derivedSrc) sourceKeys.add(derivedSrc);

          const start = coerceToDate((o as any)?.startDate);
          const end = coerceToDate((o as any)?.endDate);
          const url = normalizeUrlForKey((o as any)?.url);
          const offeringId = String((o as any)?.variationKey || "").trim();
          const numDaysRaw =
            (o as any)?.numDaysCalc ??
            (o as any)?.numDaysOverride ??
            (start && end ? numDaysFromDatesUtcInclusive(start, end) : null);
          const k = buildTier1IdempotencyKey({
            locationId: locId,
            offeringId,
            startDate: start,
            endDate: end,
            numDays: numDaysRaw,
            url,
          });
          if (k) tier1Keys.add(k);
        }

        const packed = { sourceKeys, tier1Keys };
        existingKeysByLocationId.set(locId, packed);
        return packed;
      };
      
      // Loop through the generated dates and create sessions
      for (const startDate of generatedDates) {
        try {
          // Admin-only extras per date (from dateListExtras)
          let rowPriceSingleDayOverride: number | null = null;
          // Per-row target location: when RowFormat provides <locationId>, create the session under that campLocation.
          let targetLocationId = locationId;
          let offeredVariationKey = "";
          const PROG_DEFAULT_SENTINEL = "<progDefault>";
          const isProgDefaultSentinel = (raw: unknown) =>
            String(raw ?? "").trim().toLowerCase() === PROG_DEFAULT_SENTINEL.toLowerCase();
          let extras: { title?: string; titleSuffix?: string; url?: string; regUrl?: string; description?: string; eligibilityNotes?: string; priceNote?: string; timingNote?: string; endDate?: Date; priceOverride?: number; priceSingleDayOverride?: number; locationId?: string; offeringId?: string } | undefined;

          try {
            const epochKey = utcEpochDayKey(startDate);
            const rawExtras = (formValues as any).dateListExtras?.[epochKey] as
              | { title?: string; titleSuffix?: string; url?: string; regUrl?: string; description?: string; eligibilityNotes?: string; priceNote?: string; timingNote?: string; endDate?: Date; priceOverride?: number; priceSingleDayOverride?: number; locationId?: string; offeringId?: string }
              | Array<{ title?: string; titleSuffix?: string; url?: string; regUrl?: string; description?: string; eligibilityNotes?: string; priceNote?: string; timingNote?: string; endDate?: Date; priceOverride?: number; priceSingleDayOverride?: number; locationId?: string; offeringId?: string }>
              | undefined;

            if (Array.isArray(rawExtras)) {
              // Multiple RowFormat rows share this date. Use the next extras
              // object in order; if we run out, fall back to the last one.
              const currentIndex = perDayRowIndex.get(epochKey) ?? 0;
              extras = rawExtras[currentIndex] ?? rawExtras[rawExtras.length - 1] ?? undefined;
              perDayRowIndex.set(epochKey, currentIndex + 1);
            } else {
              // Legacy / single-row behavior: a single extras object for the date
              extras = rawExtras;
            }

            // Per-row locationId: save this row's session under that campLocation when provided.
            if (extras?.locationId && String(extras.locationId).trim()) {
              targetLocationId = String(extras.locationId).trim();
            }
            if (extras?.offeringId && String(extras.offeringId).trim()) {
              offeredVariationKey = String(extras.offeringId).trim();
            }
            if (extras?.priceSingleDayOverride !== undefined && extras.priceSingleDayOverride !== null) {
              rowPriceSingleDayOverride = extras.priceSingleDayOverride;
            }
          } catch (e) {
            console.warn("[useBulkSessionsCreation] Failed to apply dateListExtras title/url/description/suffix", e);
          }

          const effectiveVariationKey = offeredVariationKey || String(formValues.variationKey || "").trim();
          if (!effectiveVariationKey) {
            throw new Error("Missing variationKey for row. Select Program or provide <offeringId>.");
          }
          const variationMap = await loadVariationMap(targetLocationId);
          const variation = variationMap.get(effectiveVariationKey);
          if (!variation) {
            throw new Error(`Variation with key ${effectiveVariationKey} not found in location ${targetLocationId}`);
          }

          // Calculate the end date based on the number of days
          // Use numDaysOverride if provided (from session length selector), otherwise use program default
          const sessionDays = formValues.numDaysOverride || variation.numDays || 1;
          const computedEndDate = calculateEndDateFromNumDays(startDate, sessionDays);


          // Prepare comprehensive offer data including all required fields
          const offerData = {
            locationId: targetLocationId,
            variationKey: effectiveVariationKey,
            displayTitle: formValues.displayTitle,
            description: formValues.description || "",
            eligibilityNotes: formValues.eligibilityNotes || "",
            url: formValues.url || "",
            regUrl: formValues.regUrl || "",
            startDate,
            endDate: computedEndDate,
            availabilityStatus: formValues.availabilityStatus,
            signupStatus: formValues.signupStatus,
            // Apply same registration events to each created session (optional)
            regEvents: (formValues as any).regEvents || null,

            // Session length fields
            numDaysOverride: formValues.numDaysOverride || null,
            // Ensure increment key is set: prefer selected override, else program's regular key
            myOfferIncrementKey: formValues.myOfferIncrementKey || variation.regularOfferIncrementKey || null,

            // Include pricing defaults from the effective variation
            priceRegular: variation.priceRegular || null,
            priceNote: variation.priceNote || "",

            // Pass through variation data for complete offer creation
            variation: variation,
            variationData: {
              ...variation,
              numDays: sessionDays,
              regularOfferIncrementKey: variation.regularOfferIncrementKey || null
            }
          } as any;

          if (extras) {
            // Determine base title precedence: if title is present (even empty), it supersedes the form value
            const baseTitleFromForm: string = offerData.displayTitle || variation.displayTitle || "";
            const hasTitleProvided = Object.prototype.hasOwnProperty.call(extras, "title");
            const newBase = hasTitleProvided ? (extras.title ?? "") : baseTitleFromForm;
            let finalTitle = (typeof newBase === "string") ? newBase.trim() : "";
            if (typeof extras.titleSuffix === "string" && extras.titleSuffix.trim().length > 0) {
              const needsSpace = finalTitle.length > 0 && !finalTitle.endsWith(" ") && !extras.titleSuffix.startsWith(" ");
              finalTitle = `${finalTitle}${needsSpace ? " " : ""}${extras.titleSuffix.trim()}`.trim();
            }
            offerData.displayTitle = finalTitle;

            // URL precedence: if url is present (even empty), supersede form value
            if (Object.prototype.hasOwnProperty.call(extras, "url")) {
              offerData.url = (extras.url ?? "").trim();
            }
            // Description precedence: if description is present (even empty), supersede form value
            if (Object.prototype.hasOwnProperty.call(extras, "description")) {
              const next = extras.description ?? "";
              offerData.description = isProgDefaultSentinel(next) ? (offerData.description || "") : next;
            }
            // Eligibility notes precedence: if eligibilityNotes is present (even empty), supersede form value
            if (Object.prototype.hasOwnProperty.call(extras, "eligibilityNotes")) {
              const next = extras.eligibilityNotes ?? "";
              offerData.eligibilityNotes = isProgDefaultSentinel(next) ? (offerData.eligibilityNotes || "") : next;
            }
            // Registration URL precedence: if regUrl is present (even empty), supersede form value
            if (Object.prototype.hasOwnProperty.call(extras, "regUrl")) {
              const next = (extras.regUrl ?? "").trim();
              offerData.regUrl = isProgDefaultSentinel(next) ? (offerData.regUrl || "") : next;
            }
            // Footnote override precedence
            if (Object.prototype.hasOwnProperty.call(extras, "priceNote")) {
              const next = extras.priceNote ?? "";
              (offerData as any).priceNoteOverride = isProgDefaultSentinel(next) ? null : String(next);
            }
            if (Object.prototype.hasOwnProperty.call(extras, "timingNote")) {
              const next = extras.timingNote ?? "";
              (offerData as any).timingNoteOverride = isProgDefaultSentinel(next) ? null : String(next);
            }
            // Price override precedence
            if (extras.priceOverride !== undefined && extras.priceOverride !== null) {
              offerData.priceOverride = extras.priceOverride;
            }
            // Override end date explicitly from RowFormat when provided
            if (extras.endDate instanceof Date && !isNaN(extras.endDate as any)) {
              offerData.endDate = extras.endDate;
            }
          }

          offerData.locationId = targetLocationId;

          // Idempotency: tier0 (WebTrac FMID -> sourceKey) + tier1 fallback (location|offering|start|end|numDays|url)
          const existingKeys = await loadExistingKeys(targetLocationId);
          const tier0SourceKey = buildTier0SourceKeyFromUrl(offerData.url);
          if (tier0SourceKey) {
            (offerData as any).sourceKey = tier0SourceKey;
          }
          const numDaysForKey = numDaysFromDatesUtcInclusive(startDate, offerData.endDate);
          const tier1Key = buildTier1IdempotencyKey({
            locationId: targetLocationId,
            offeringId: effectiveVariationKey,
            startDate,
            endDate: offerData.endDate,
            numDays: numDaysForKey,
            url: offerData.url,
          });

          const alreadyExists =
            (tier0SourceKey ? existingKeys.sourceKeys.has(tier0SourceKey) : false) ||
            (tier1Key ? existingKeys.tier1Keys.has(tier1Key) : false);

          if (alreadyExists) {
            skippedCount++;
            continue;
          }


          // Use the proper service to create the offer (per-row target when RowFormat has <locationId>)
          await createOffer(db, targetLocationId, offerData, userId);
          successCount++;
          if (tier0SourceKey) existingKeys.sourceKeys.add(tier0SourceKey);
          if (tier1Key) existingKeys.tier1Keys.add(tier1Key);

          // Also create single-day sessions for all overlapping weekdays if selected
          if (formValues.createSingleDaysForOverlaps) {
            const singleDayDates = generateSessionDates(startDate, offerData.endDate, "weekdays");
            for (const singleDate of singleDayDates) {
              try {
                const singleOfferData = {
                  ...offerData,
                  startDate: singleDate,
                  endDate: singleDate,
                  numDaysOverride: 1,
                  // Prefer explicit 1-day increment key if available on program
                  myOfferIncrementKey: (variation.regularOfferIncrementAlternatives || []).find(a => a.key === "oi_1day")?.key || offerData.myOfferIncrementKey || null
                };
                // NEW: Apply single-day specific price override when provided
                if (rowPriceSingleDayOverride !== null) {
                  (singleOfferData as any).priceOverride = rowPriceSingleDayOverride;
                }
                // Apply optional suffix to single-day session title
                try {
                  const suffix = (formValues as any).singleDayTitleSuffix;
                  if (typeof suffix === "string" && suffix.trim().length > 0) {
                    const baseTitle: string = singleOfferData.displayTitle || "";
                    const needsSpace = baseTitle.length > 0 && !baseTitle.endsWith(" ") && !suffix.startsWith(" ");
                    singleOfferData.displayTitle = `${baseTitle}${needsSpace ? " " : ""}${suffix}`.trim();
                  }
                } catch {
                  // no-op
                }
                // Single-day sessions are synthetic — do not assign tier0 sourceKey (would conflict with vendor identity).
                (singleOfferData as any).sourceKey = null;
                const existingKeys2 = await loadExistingKeys(targetLocationId);
                const tier1Key2 = buildTier1IdempotencyKey({
                  locationId: targetLocationId,
                  offeringId: effectiveVariationKey,
                  startDate: singleDate,
                  endDate: singleDate,
                  numDays: 1,
                  url: singleOfferData.url,
                });
                if (tier1Key2 && existingKeys2.tier1Keys.has(tier1Key2)) {
                  skippedCount++;
                  continue;
                }

                await createOffer(db, targetLocationId, singleOfferData, userId);
                successCount++;
                if (tier1Key2) existingKeys2.tier1Keys.add(tier1Key2);
              } catch (error) {
                console.error(`Error creating single-day session for ${singleDate.toISOString()}:`, error);
                failedDates.push(singleDate);
              }
            }
          }
          
          // Log successful creation
        } catch (error) {
          console.error(`Error creating bulk session for date ${startDate.toISOString()}:`, error);
          failedDates.push(startDate);
        }
      }
      
      // Dismiss loading toast if it exists
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      
      // Show final status toast
      if (successCount > 0) {
        toast.success(
          `Successfully created ${successCount} sessions` +
            (skippedCount > 0 ? ` (${skippedCount} skipped as already exists)` : "")
        );
      }
      if (successCount === 0 && skippedCount > 0 && failedDates.length === 0) {
        toast.success(`No sessions created (${skippedCount} skipped as already exists)`);
      }
      
      // Show error for failed sessions if any
      if (failedDates.length > 0) {
        toast.error(`Failed to create ${failedDates.length} sessions`);
        console.error("Failed to create sessions for dates:", failedDates);
      }
      
      // Call the onSuccess callback
      if (successCount > 0) {
        onSuccess();
      }
    } catch (error: any) {
      // Show an error message
      toast.error(error.message || "Failed to create sessions. Please try again.");
      console.error("Error in bulk session creation:", error);
    } finally {
      setIsCreating(false);
    }
  };
  
  return { createBulkSessions, isCreating };
};
